class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.cachedPlayerResults = {};
    // this.cachedTicketResults = {};
    this.cachedNewsResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.playerAbortController = new AbortController();
    // this.ticketAbortController = new AbortController();
    this.newsAbortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.playerAbortController.abort();
      this.playerAbortController = new AbortController();
      // this.ticketAbortController.abort();
      // this.ticketAbortController = new AbortController();
      this.newsAbortController.abort();
      this.newsAbortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]",
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    this.shopifyResultsLoaded = false;
    this.pendingPlayerData = null;
    this.pendingNewsData = null;
    this._playerNoResults = false;
    this._newsNoResults = false;

    const shopifyPromise = this.cachedResults[queryKey]
      ? Promise.resolve(this.cachedResults[queryKey])
      : this.fetchShopifyResults(searchTerm, queryKey);

    shopifyPromise
      .then((shopifyMarkup) => {
        this.renderShopifyResults(shopifyMarkup);
        this.shopifyResultsLoaded = true;

        if (this.pendingPlayerData !== null) {
          if (this.pendingPlayerData.length > 0) {
            this.renderPlayerResults(this.pendingPlayerData);
          } else {
            this.hidePlayersSkeleton();
          }
          this.pendingPlayerData = null;
        }
        if (this.pendingNewsData !== null) {
          if (this.pendingNewsData.length > 0) {
            this.renderNewsResults(this.pendingNewsData);
          } else {
            this.hideNewsSkeleton();
          }
          this.pendingNewsData = null;
        }
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });

    const playerPromise = this.cachedPlayerResults[queryKey]
      ? Promise.resolve(this.cachedPlayerResults[queryKey])
      : this.fetchPlayerResults(searchTerm, queryKey);

    playerPromise
      .then((playerData) => {
        if (playerData && playerData.data && playerData.data.length > 0) {
          if (this.shopifyResultsLoaded) {
            this.renderPlayerResults(playerData.data);
          } else {
            this.pendingPlayerData = playerData.data;
          }
        } else {
          if (this.shopifyResultsLoaded) {
            this.hidePlayersSkeleton();
          } else {
            this.pendingPlayerData = [];
          }
        }
      })
      .catch(() => {
        if (this.shopifyResultsLoaded) {
          this.hidePlayersSkeleton();
        } else {
          this.pendingPlayerData = [];
        }
      });

    const newsPromise = this.cachedNewsResults[queryKey]
      ? Promise.resolve(this.cachedNewsResults[queryKey])
      : this.fetchNewsResults(searchTerm, queryKey);

    newsPromise
      .then((newsData) => {
        if (newsData && newsData.data && newsData.data.length > 0) {
          if (this.shopifyResultsLoaded) {
            this.renderNewsResults(newsData.data);
          } else {
            this.pendingNewsData = newsData.data;
          }
        } else {
          if (this.shopifyResultsLoaded) {
            this.hideNewsSkeleton();
          } else {
            this.pendingNewsData = [];
          }
        }
      })
      .catch(() => {
        if (this.shopifyResultsLoaded) {
          this.hideNewsSkeleton();
        } else {
          this.pendingNewsData = [];
        }
      });
  }

  fetchShopifyResults(searchTerm, queryKey) {
    return fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm,
      )}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      },
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );
        return resultsMarkup;
      });
  }

  fetchPlayerResults(searchTerm, queryKey) {
    if (
      typeof API_URL === "undefined" ||
      typeof API_Header_Options === "undefined"
    ) {
      return Promise.resolve({ data: [] });
    }

    // Served from crm-sorts, not b2cs: b2cs exposes the full personal record
    // (address, phone, birth date, documents) and answers unauthenticated.
    // crm-sorts has no name filter, so aicoSearchPeople matches over the squad
    // list it loads once per page view. No abort signal is needed — the list is
    // fetched at most once and later keystrokes resolve from memory.
    if (typeof window.aicoSearchPeople !== "function") {
      return Promise.resolve({ data: [] });
    }

    return window.aicoSearchPeople(searchTerm, 5)
      .then((data) => {
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedPlayerResults[queryKey] = data;
          },
        );
        return data;
      })
      .catch(() => {
        return { data: [] };
      });
  }

  // fetchTicketResults(searchTerm, queryKey) {
  //   if (
  //     typeof API_URL === "undefined" ||
  //     typeof API_Header_Options === "undefined"
  //   ) {
  //     return Promise.resolve({ data: [] });
  //   }

  //   const params = new URLSearchParams();
  //   params.append("filter[isLightVersion]", "1");
  //   params.append("filter[searchTerm]", searchTerm);

  //   const fetchOptions = {
  //     ...API_Header_Options,
  //     signal: this.ticketAbortController.signal,
  //   };

  //   return fetch(
  //     `${API_URL}match-center/soccer-matches?${params.toString()}`,
  //     fetchOptions
  //   )
  //     .then((response) => {
  //       if (!response.ok) {
  //         return { data: [] };
  //       }
  //       return response.json();
  //     })
  //     .then((data) => {
  //       this.allPredictiveSearchInstances.forEach(
  //         (predictiveSearchInstance) => {
  //           predictiveSearchInstance.cachedTicketResults[queryKey] = data;
  //         }
  //       );
  //       return data;
  //     })
  //     .catch(() => {
  //       return { data: [] };
  //     });
  // }

  fetchNewsResults(searchTerm, queryKey) {
    if (
      typeof API_URL === "undefined" ||
      typeof API_Header_Options === "undefined"
    ) {
      return Promise.resolve({ data: [] });
    }

    const params = new URLSearchParams();
    params.append("page[number]", "1");
    params.append("page[size]", "5");
    params.append("filter[isActive]", "1");
    params.append("filter[isLightVersion]", "1");
    params.append("sort", "-publishDate");
    params.append("filter[searchTerm]", searchTerm);

    const fetchOptions = {
      ...API_Header_Options,
      signal: this.newsAbortController.signal,
    };

    return fetch(`${API_URL}news?${params.toString()}`, fetchOptions)
      .then((response) => {
        if (!response.ok) {
          return { data: [] };
        }
        return response.json();
      })
      .then((data) => {
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedNewsResults[queryKey] = data;
          },
        );
        return data;
      })
      .catch(() => {
        return { data: [] };
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderShopifyResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    if (this._playerNoResults) this.hidePlayersSkeleton();
    if (this._newsNoResults) this.hideNewsSkeleton();

    this.setAttribute("results", true);
    this.setLiveRegionResults();
    this.open();
  }

  renderPlayerResults(players) {
    const playersContainer = this.querySelector(
      "#predictive-search-players-container",
    );
    if (!playersContainer) return;

    const playersList = playersContainer.querySelector(
      "#predictive-search-results-players-list",
    );
    if (!playersList) return;

    // Clear existing content
    playersList.innerHTML = "";

    players.forEach((player, index) => {
      const attrs = player.attributes;
      const firstName = attrs.firstName || "";
      const lastName = attrs.lastName || "";
      const fullName = `${firstName} ${lastName}`.trim();
      const image = attrs.image || "";
      const position =
        attrs.translations?.[0]?.position || attrs.position || "";
      // No FCRJ person has a profileUrl and the search rows carry no team:
      // start at the teams overview and swap in the detail page once
      // aicoPersonPageUrl (snippets/aico-people-js) resolves the team.
      const teamsPageUrl =
        typeof window.aicoTeamsPageUrl === "string" && window.aicoTeamsPageUrl
          ? window.aicoTeamsPageUrl
          : "/pages/teams";
      const profileUrl = attrs.profileUrl
        ? `/pages/kader/${attrs.profileUrl}`
        : teamsPageUrl;

      // aicoJerseyNumber handles jsNumber, every label spelling
      // ("Trikotnummer", "Rückennummer", ...) and the stopgap table.
      const jerseyNumber = window.aicoJerseyNumber
        ? window.aicoJerseyNumber({ entity: { id: player.id, ...attrs } })
        : "";

      const listItem = document.createElement("li");
      listItem.id = `predictive-search-option-player-${index + 1}`;
      listItem.className = "predictive-search__list-item";
      listItem.setAttribute("role", "option");
      listItem.setAttribute("aria-selected", "false");

      listItem.innerHTML = `
        <a href="${aicoSafeUrl(profileUrl)}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
          ${
            image
              ? `<img class="predictive-search__image predictive-search__image--player" src="${aicoSafeUrl(image)}" alt="${aicoEscapeHtml(fullName)}" width="50" height="50" loading="lazy">`
              : '<div class="predictive-search__image predictive-search__image--player predictive-search__image--placeholder"></div>'
          }
          <div class="predictive-search__item-content">
            ${
              jerseyNumber
                ? `<span class="predictive-search__item-player-number">#${aicoEscapeHtml(jerseyNumber)}</span>`
                : ""
            }
            <p class="predictive-search__item-heading h5">${aicoEscapeHtml(fullName)}</p>
            ${
              position
                ? `<span class="predictive-search__item-player-position">${aicoEscapeHtml(position)}</span>`
                : ""
            }
          </div>
        </a>
      `;

      playersList.appendChild(listItem);
      if (typeof window.aicoPersonPageUrl === "function") {
        const anchor = listItem.querySelector("a");
        window.aicoPersonPageUrl(player.id, teamsPageUrl).then((url) => {
          if (url && anchor.isConnected) anchor.setAttribute("href", url);
        });
      }
    });

    playersContainer.setAttribute("data-loaded", "true");
  }

  hidePlayersSkeleton() {
    this._playerNoResults = true;
    const playersContainer = this.querySelector(
      "#predictive-search-players-container",
    );
    if (playersContainer) playersContainer.remove();
  }

  // renderTicketResults(matches) {
  //   const ticketsContainer = this.querySelector(
  //     "#predictive-search-tickets-container"
  //   );
  //   if (!ticketsContainer) return;

  //   const ticketsList = ticketsContainer.querySelector(
  //     "#predictive-search-results-tickets-list"
  //   );
  //   if (!ticketsList) return;

  //   ticketsList.innerHTML = "";

  //   matches.forEach((match, index) => {
  //     const matchName = match.matchName || "";
  //     const ticketsUrl = match.ticketsUrl || null;
  //     const matchDate = match.date || "";
  //     const status = match.status || "";

  //     let formattedDate = "";
  //     if (matchDate) {
  //       const date = new Date(matchDate);
  //       formattedDate = date.toLocaleDateString("de-CH", {
  //         day: "2-digit",
  //         month: "2-digit",
  //         year: "numeric",
  //         hour: "2-digit",
  //         minute: "2-digit",
  //       });
  //     }

  //     const listItem = document.createElement("li");
  //     listItem.id = `predictive-search-option-ticket-${index + 1}`;
  //     listItem.className = "predictive-search__list-item";
  //     listItem.setAttribute("role", "option");
  //     listItem.setAttribute("aria-selected", "false");

  //     if (ticketsUrl) {
  //       listItem.innerHTML = `
  //         <a href="${ticketsUrl}" class="predictive-search__item predictive-search__item--ticket link link--text" tabindex="-1" target="_blank" rel="noopener">
  //           <div class="predictive-search__item-content">
  //             <p class="predictive-search__item-heading h5">${this.escapeHtml(
  //               matchName
  //             )}</p>
  //             <span class="predictive-search__item-ticket-date">${formattedDate}</span>
  //           </div>
  //           <span class="predictive-search__item-ticket-badge">Tickets</span>
  //         </a>
  //       `;
  //       ticketsList.appendChild(listItem);
  //     }
  //   });

  //   if (ticketsList.children.length > 0) {
  //     ticketsContainer.style.display = "block";
  //   }
  // }

  renderNewsResults(newsItems) {
    const newsContainer = this.querySelector(
      "#predictive-search-news-container",
    );
    if (!newsContainer) return;

    const newsList = newsContainer.querySelector(
      "#predictive-search-results-news-list",
    );
    if (!newsList) return;

    newsList.innerHTML = "";

    newsItems.forEach((news, index) => {
      const attrs = news.attributes;
      const title = attrs.name || attrs.translations?.[0]?.name || "";
      const image =
        attrs.optimizedListImage || attrs.optimizedImage || attrs.image || "";
      const urlHandle =
        attrs.urlHandle || attrs.translations?.[0]?.urlHandle || "";
      const publishDate = attrs.publishDate || "";

      // Format date
      let formattedDate = "";
      if (publishDate) {
        const date = new Date(publishDate);
        formattedDate = date.toLocaleDateString("de-CH", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }

      const newsUrl = urlHandle
        ? `/pages/news/${urlHandle}`
        : "#";

      const listItem = document.createElement("li");
      listItem.id = `predictive-search-option-news-${index + 1}`;
      listItem.className = "predictive-search__list-item";
      listItem.setAttribute("role", "option");
      listItem.setAttribute("aria-selected", "false");

      listItem.innerHTML = `
        <a href="${newsUrl}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
          ${
            image
              ? `<img class="predictive-search__image predictive-search__image--news" src="${image}" alt="${this.escapeHtml(
                  title,
                )}" width="50" height="50" loading="lazy">`
              : '<div class="predictive-search__image predictive-search__image--news predictive-search__image--placeholder"></div>'
          }
          <div class="predictive-search__item-content">
            <p class="predictive-search__item-heading h5">${this.escapeHtml(
              title,
            )}</p>
            <span class="predictive-search__item-news-date">${formattedDate}</span>
          </div>
        </a>
      `;

      newsList.appendChild(listItem);
    });

    newsContainer.setAttribute("data-loaded", "true");
  }

  hideNewsSkeleton() {
    this._newsNoResults = true;
    const newsContainer = this.querySelector(
      "#predictive-search-news-container",
    );
    if (newsContainer) newsContainer.remove();
  }

  escapeHtml(str) {
    if (!str) return "";
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header")?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (
      !this.predictiveSearchResults.classList.contains(
        "predictive-search--header",
      )
    ) {
      this.predictiveSearchResults.style.maxHeight =
        this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);
